import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupabase } from '@/lib/supabase';
import { useAuthReady } from './useAuthReady';
import { translateText, type SupportedLanguage } from '@/lib/translation';
import type { Message, ChatConversation } from '@/types/database';
import DOMPurify from 'dompurify';

// Query keys
export const messageKeys = {
  all: ['messages'] as const,
  conversations: (landlordId: string) => [...messageKeys.all, 'conversations', landlordId] as const,
  byBooking: (bookingId: string) => [...messageKeys.all, 'booking', bookingId] as const,
  unreadCount: (landlordId: string) => [...messageKeys.all, 'unread', landlordId] as const,
};

/**
 * The host's conversations (one per booking; a group booking is ONE
 * conversation). Trashed bookings are left out by the server.
 */
export function useConversations(landlordId: string | undefined) {
  return useQuery({
    queryKey: messageKeys.conversations(landlordId || ''),
    queryFn: async () => {
      if (!landlordId) return [];

      const { data, error } = await getSupabase().rpc('get_landlord_conversations');

      if (error) {
        console.error('Error fetching conversations:', error);
        throw error;
      }

      return (data || []) as ChatConversation[];
    },
    enabled: !!landlordId,
    refetchInterval: 5000, // Refresh every 5 seconds for better UX
  });
}

/**
 * "New message": current and upcoming stays that have no message yet
 * (group bookings collapsed into one entry).
 */
export function useChatCandidates(landlordId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...messageKeys.all, 'candidates', landlordId || ''] as const,
    queryFn: async () => {
      const { data, error } = await getSupabase().rpc('get_landlord_chat_candidates');
      if (error) throw error;
      return (data || []) as ChatConversation[];
    },
    enabled: !!landlordId && enabled,
    staleTime: 30_000,
  });
}

/**
 * Hook za dohvat poruka za određenu rezervaciju
 */
export function useBookingMessages(bookingId: string | undefined) {
  return useQuery({
    queryKey: messageKeys.byBooking(bookingId || ''),
    queryFn: async () => {
      if (!bookingId) return [];

      const { data, error } = await getSupabase()
        .from('messages')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        throw error;
      }

      return (data || []) as Message[];
    },
    enabled: !!bookingId,
  });
}

/**
 * Hook za broj nepročitanih poruka
 */
export function useUnreadCount(landlordId: string | undefined) {
  // The header passes the Clerk user id, which exists before the database
  // token does — the RPC refuses anonymous callers (42501), so wait for it.
  const { isReady } = useAuthReady();
  return useQuery({
    queryKey: messageKeys.unreadCount(landlordId || ''),
    queryFn: async () => {
      if (!landlordId) return 0;
      // Same conversations as the Messages page (trashed bookings excluded).
      const { data, error } = await getSupabase().rpc('get_landlord_unread_total');
      if (error) {
        console.error('Error fetching unread count:', error);
        return 0;
      }
      return typeof data === 'number' ? data : Number(data) || 0;
    },
    enabled: !!landlordId && isReady,
    refetchInterval: 10000, // Refresh every 10 seconds
  });
}

/**
 * Hook za slanje poruke kao iznajmljivač
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bookingId,
      content,
      senderId,
      senderLanguage,
      targetLanguage,
    }: {
      bookingId: string;
      content: string;
      senderId: string;
      senderLanguage: SupportedLanguage;
      targetLanguage: SupportedLanguage;
    }) => {
      // Sanitize content
      const sanitizedContent = DOMPurify.sanitize(content.trim(), {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      });

      if (!sanitizedContent) {
        throw new Error('Empty message');
      }

      // Translate if different languages
      let translatedContent = null;
      if (senderLanguage !== targetLanguage) {
        const translation = await translateText(sanitizedContent, senderLanguage, targetLanguage);
        if (translation.success) {
          translatedContent = translation.translatedText;
        }
      }

      // Insert message
      const { data, error } = await getSupabase()
        .from('messages')
        .insert({
          booking_id: bookingId,
          sender_type: 'landlord',
          sender_id: senderId,
          content: translatedContent || sanitizedContent, // Display translated version
          original_content: sanitizedContent,
          source_language: senderLanguage,
          translated_content: translatedContent,
          target_language: targetLanguage,
          is_read: false,
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        throw error;
      }

      return data as Message;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: messageKeys.byBooking(data.booking_id) });
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}

/**
 * Hook za označavanje poruka kao pročitane
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId }: { bookingId: string }) => {
      const { error } = await getSupabase()
        .from('messages')
        .update({ is_read: true })
        .eq('booking_id', bookingId)
        .eq('sender_type', 'guest')
        .eq('is_read', false);

      if (error) {
        console.error('Error marking messages as read:', error);
        throw error;
      }

      return bookingId;
    },
    onSuccess: (bookingId) => {
      queryClient.invalidateQueries({ queryKey: messageKeys.byBooking(bookingId) });
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
}

/**
 * Helper za formatiranje vremena poruke
 */
export function formatMessageTime(dateStr: string, locale: string = 'hr-HR'): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return locale === 'hr-HR' ? 'Upravo sada' : 'Just now';
  }

  if (diffMins < 60) {
    return locale === 'hr-HR' ? `Prije ${diffMins} min` : `${diffMins} min ago`;
  }

  if (diffHours < 24) {
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }

  if (diffDays < 7) {
    return date.toLocaleDateString(locale, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

/**
 * Helper za grupiranje poruka po datumu
 */
export function groupMessagesByDate(messages: Message[]): Record<string, Message[]> {
  return messages.reduce((groups, message) => {
    const date = new Date(message.created_at).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {} as Record<string, Message[]>);
}
